import { INITIATOR_USER, Severities } from './constants'
import type { EventLogConfig, EventLogUser, EventType, Severity, Status } from './types'

interface LogFeatureEventInput {
    eventType: EventType
    status?: Status
    user: EventLogUser
    internalUser?: boolean
    excludeInMetrics?: boolean
    initiator?: string
    referenceId: string
    metadata?: unknown
    severity?: Severity
}

interface LogAuthEventInput {
    eventType: EventType
    status?: Status
    user: EventLogUser
    internalUser?: boolean
    excludeInMetrics?: boolean
    initiator?: string
    severity?: Severity
    mobileDevice?: boolean
    loginMethod?: string
    logoutReason?: string
}

interface LogSystemFeatureEventInput {
    eventType: EventType
    status?: Status
    referenceId: string
    severity?: Severity
    initiator?: string
    metadata?: Record<string, unknown>
}

const SYSTEM_USER: EventLogUser = { id: 'system', email: 'system@local' }

export class EventLogger {
    private config: EventLogConfig

    constructor(config: EventLogConfig) {
        this.config = config
    }

    async register({ user, initiator, severity }: { user: EventLogUser; initiator?: string; severity?: Severity }) {
        if (!this.config.isEnabled()) return

        try {
            await this.createAuthEvent({
                eventType: 'USER_REGISTER',
                user,
                initiator,
                severity,
            })
        } catch (error) {
            this.config.onError(error, { message: 'Error logging register event', section: 'EventLog' })
        }
    }

    async login({ user, initiator, severity }: { user: EventLogUser; initiator?: string; severity?: Severity }) {
        if (!this.config.isEnabled()) return

        try {
            await this.createAuthEvent({
                eventType: 'USER_LOGIN',
                user,
                initiator,
                severity,
            })
        } catch (error) {
            this.config.onError(error, { message: 'Error logging login event', section: 'EventLog' })
        }
    }

    async logout({
        user,
        initiator,
        logoutReason = '',
        severity,
    }: {
        user: EventLogUser | undefined
        initiator?: string
        logoutReason?: string
        severity?: Severity
    }) {
        if (!this.config.isEnabled()) return

        try {
            await this.createAuthEvent({
                eventType: 'USER_LOGOUT',
                user: user || { id: 'notSet', email: 'notSet' },
                initiator,
                logoutReason,
                severity,
            })
        } catch (error) {
            this.config.onError(error, { message: 'Error logging logout event', section: 'EventLog' })
        }
    }

    async logFeatureEvent({
        eventType,
        status,
        user,
        internalUser,
        excludeInMetrics,
        initiator,
        referenceId,
        metadata,
        severity,
    }: LogFeatureEventInput) {
        if (!this.config.isEnabled()) return

        try {
            // Single nested create — fixes N+1 (was 2 separate queries before)
            await this.config.dbClient.event.create({
                data: {
                    eventType,
                    status: status || 'DONE',
                    severity: severity || 'INFO',
                    application: this.config.application,
                    environment: this.config.getEnvironment(),
                    userId: user.id || 'unknown',
                    internalUser: internalUser || false,
                    excludeInMetrics: excludeInMetrics || false,
                    initiator: initiator || INITIATOR_USER,
                    featureEvent: {
                        create: {
                            referenceId,
                            metadata: metadata || {},
                        },
                    },
                },
            })
        } catch (error) {
            this.config.onError(error, { message: 'Error in logFeatureEvent', section: 'EventLog' })
        }
    }

    async logFeatureEventsBatch(events: LogFeatureEventInput[]) {
        if (!this.config.isEnabled() || events.length === 0) return

        try {
            await this.config.dbClient.$transaction(
                events.map((event) =>
                    this.config.dbClient.event.create({
                        data: {
                            eventType: event.eventType,
                            status: event.status || 'DONE',
                            severity: event.severity || 'INFO',
                            application: this.config.application,
                            environment: this.config.getEnvironment(),
                            userId: event.user.id || 'unknown',
                            internalUser: event.internalUser || false,
                            excludeInMetrics: event.excludeInMetrics || false,
                            initiator: event.initiator || INITIATOR_USER,
                            featureEvent: {
                                create: {
                                    referenceId: event.referenceId,
                                    metadata: event.metadata || {},
                                },
                            },
                        },
                    }),
                ),
            )
        } catch (error) {
            this.config.onError(error, { message: 'Error in logFeatureEventsBatch', section: 'EventLog' })
        }
    }

    async logSystemFeatureEvent({
        eventType,
        status,
        referenceId,
        severity,
        initiator,
        metadata,
    }: LogSystemFeatureEventInput) {
        if (!this.config.isEnabled()) return

        try {
            await this.logFeatureEvent({
                eventType,
                status,
                user: SYSTEM_USER,
                referenceId,
                initiator: initiator || 'system',
                severity: severity || Severities.ERROR,
                metadata,
            })
        } catch (error) {
            this.config.onError(error, { message: 'Error in logSystemFeatureEvent', section: 'EventLog' })
        }
    }

    private async createAuthEvent(input: LogAuthEventInput) {
        // Single nested create — fixes N+1
        await this.config.dbClient.event.create({
            data: {
                eventType: input.eventType,
                status: input.status || 'DONE',
                severity: input.severity || 'INFO',
                application: this.config.application,
                environment: this.config.getEnvironment(),
                userId: input.user.id || 'unknown',
                internalUser: input.internalUser || false,
                excludeInMetrics: input.excludeInMetrics || false,
                initiator: input.initiator || INITIATOR_USER,
                authEvent: {
                    create: {
                        mobileDevice: input.mobileDevice || false,
                        loginMethod: this.config.loginProvider,
                        logoutReason: input.logoutReason || null,
                    },
                },
            },
        })
    }
}

export function createEventLogger(config: EventLogConfig): EventLogger {
    return new EventLogger(config)
}
